import i18n, { SUPPORTED_LOCALES, DEFAULT_LOCALE, SupportedLocale } from '../i18n';

const isSupported = (value: string): value is SupportedLocale =>
  (SUPPORTED_LOCALES as readonly string[]).includes(value);

/**
 * Single client-side application point for the active locale.
 *
 * Normalizes any incoming value (coerce to string, lowercase, drop region
 * suffixes such as `-ES` / `_FR`), validates it against the supported set and
 * resolves to the default locale (`fr`) when unsupported. When the resolved
 * locale differs from the active one it is persisted to `localStorage['lang']`
 * and applied via `i18n.changeLanguage`. Always returns the resolved locale.
 *
 * Never calls `i18n.changeLanguage` with an unvalidated value and never emits
 * any postMessage.
 */
export const applyLocale = (value: string): SupportedLocale => {
  const normalized = String(value ?? '')
    .toLowerCase()
    .split(/[-_]/)[0];

  const resolved: SupportedLocale = isSupported(normalized) ? normalized : DEFAULT_LOCALE;

  if (i18n.language !== resolved) {
    localStorage.setItem('lang', resolved);
    i18n.changeLanguage(resolved);
  }

  return resolved;
};

/**
 * Logout hook-point: the locale is device-persisted, not per-user, so it must
 * be dropped at logout or the next account inherits the previous user's
 * language. Removes `localStorage['lang']` and returns i18n to the default.
 */
export const clearLocale = (): void => {
  localStorage.removeItem('lang');
  if (i18n.language !== DEFAULT_LOCALE) {
    i18n.changeLanguage(DEFAULT_LOCALE);
  }
};
